<script>
import LoadingState from '../../../../components/widgets/LoadingState.vue';
import KnowledgeCard from './KnowledgeCard.vue';
import DeleteDocumentModal from './DeleteDocumentModal.vue';
import { format, parseISO } from 'date-fns';

export default{
  name:"DynamicList",
  props:{
    listType: {
      type: String,
      required: true
    },
    list: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    isError: {
      type: Boolean,
      required: true
    }
  },
  components: {
    LoadingState,
    KnowledgeCard,
    DeleteDocumentModal,
  },
  data(){
    return{
      selectedStatus: 'list',
      localList: this.list,
      isShowDeleteModal: false,
      deleteObj: null,
      searchQuery: '',
      selectedSortOption: 'nameAsc',
      sortOptions: [
        { value: 'nameAsc', label: this.$t('KNOWLEDGE.SORT_NAME_ASC') },
        { value: 'nameDesc', label: this.$t('KNOWLEDGE.SORT_NAME_DESC') },
      ],

      isTableView: false,
      isLocalLoading: false,
      isLocalError: false,
    }
  },
  computed: {
    sortedList(){
      if(this.selectedSortOption === 'nameAsc'){
        return this.localList.sort((a, b) => a.title.localeCompare(b.title))
      }else if(this.selectedSortOption === 'nameDesc'){
        return this.localList.sort((a, b) => b.title.localeCompare(a.title))
      }else{
        return this.localList
      }
    },
    filteredList(){
      const query = this.searchQuery.toLowerCase();
      return this.sortedList.filter( item => {
        return( item.title.toLowerCase().includes(query) ||
          item.sync_status.toLowerCase().includes(query) ||
          item.created_at.toLowerCase().includes(query))
      })
    }
  },
  watch: {
    list: {
      handler(newList) {
        // Deep clone the array to ensure reactivity
        this.localList = JSON.parse(JSON.stringify(newList));
        // Reset search and sort when list changes
        this.searchQuery = '';
        this.selectedSortOption = 'nameAsc';
      },
      deep: true
    },
    isLoading: {
      handler(val) {
        this.isLocalLoading = val;
      },
      immediate: true
    },
    isError: {
      handler(val) {
        this.isLocalError = val;
      },
      immediate: true
    }
  },
  methods: {
    handleDelete(obj){
      this.deleteObj = obj;
      this.isShowDeleteModal = true;
    },
    handleCloseDelete(){
      this.deleteObj = null;
      this.isShowDeleteModal = false;
    },
    clearSearchQuery(){
      this.searchQuery = ''
    },
    handleCardClick(item){
      this.$emit('clicked', item)
    },
    formatDate(date){
      const isoDate = parseISO(date);
      const formatted = format(isoDate, "dd/MM/yyyy, hh:mm a");
      return formatted
    }
  }
}
</script>

<template>
  <div class="w-full">
    <!-- Modals -->
    <DeleteDocumentModal v-if="isShowDeleteModal" :delete-obj="deleteObj" @close="handleCloseDelete" @refresh="$emit('refresh')" />

    <!-- Search Query -->
    <div class="flex flex-col md:flex-row gap-4 mb-6 items-start md:items-center">
      <div class="relative flex-grow max-w-md">
        <div v-if="!searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10 pointer-events-none">
          <fluent-icon icon="search" class="h-5 w-5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
        </div>
        <button
          type="button"
          v-if="searchQuery"
          @click="clearSearchQuery"
          class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10"
        >
          <fluent-icon icon="dismiss" class="h-5 w-5 text-red-400 hover:text-red-600 dark:text-red-300 dark:hover:text-red-400" />
        </button>
        <input
          type="text"
          class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg py-2 pl-10 pr-10 w-full focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
          :placeholder="`Search ${listType || 'list'}...`"
          v-model="searchQuery"
        />
      </div>
      
      <div class="relative flex-shrink-0 w-full md:w-auto">
        <select
          v-model="selectedSortOption"
          class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg py-2 px-4 w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all appearance-none cursor-pointer pr-10 text-slate-900 dark:text-slate-100"
          aria-label="Sort knowledge bases"
        >
          <option v-for="option in sortOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-if="searchQuery" class="text-sm text-slate-600 dark:text-slate-400 flex-shrink-0">
        {{ filteredList.length }} {{ filteredList.length > 1 ? 'results' : 'result' }}
      </div>

      <div class="border border-slate-300 dark:border-slate-700 rounded cursor-pointer flex mb-4 overflow-hidden">
        <div class="p-2 border-r border-slate-300 dark:border-slate-700" :class="{'bg-woot-500 text-white': !isTableView, 'dark:bg-slate-800 dark:text-slate-200': isTableView}" @click.stop="isTableView = false">
          <fluent-icon icon="document" class="h-5 w-5" />
        </div>
        <div class="p-2" :class="{'bg-woot-500 text-white': isTableView, 'dark:bg-slate-800 dark:text-slate-200': !isTableView}" @click.stop="isTableView = true">
          <fluent-icon icon="table-switch" class="h-5 w-5" />
        </div>
      </div>
    </div>
    
    <!-- Error State -->
    <div v-if="isLocalError && !isLocalLoading" class="bg-slate-100 dark:bg-slate-800 rounded w-full min-h-[300px] flex flex-col gap-4 items-center justify-center">
      <fluent-icon icon="warning" size="48" class="text-red-500 dark:text-red-400" />
      <h1 class="text-slate-900 dark:text-slate-200">Error loading list. Please try again later.</h1>
    </div>

    <!-- Empty State -->
    <div v-if="filteredList.length <= 0 && !isLocalError && !isLocalLoading" class="bg-slate-100 dark:bg-slate-800 rounded w-full min-h-[300px] flex flex-col gap-4 items-center justify-center">
      <fluent-icon icon="document" size="48" class="text-slate-600 dark:text-slate-400" />
      <h1 class="text-slate-900 dark:text-slate-200">The list is empty.</h1>
    </div>

    <!-- Loading State -->
    <div v-if="isLocalLoading" class="bg-slate-100 dark:bg-slate-800 rounded w-full min-h-[300px] flex flex-col items-center justify-center">
      <LoadingState message="Loading list..." />
    </div>
    
    <div v-if="!isLocalLoading && !isLocalError && filteredList.length > 0">
      <!-- List State -->
      <div v-if="!isTableView" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="item in filteredList" :key="item.id" @click="handleCardClick(item)">
          <KnowledgeCard :item="item" @delete="handleDelete" />
        </div>
      </div>

      <!-- Table View -->
      <div v-if="isTableView" class="overflow-x-auto rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
        <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <!-- Table Head -->
          <thead class="bg-slate-100 dark:bg-slate-900">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Title</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Status</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Retry Count</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Last Sync</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Created</th>
              <th scope="col" class="px-6 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>

          <!-- Table Body -->
          <tbody class="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
            <tr
              v-for="item in filteredList"
              :key="item.id"
              class="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition"
              @click="handleCardClick(item)"
            >
              <!-- Title -->
              <td class="px-6 py-4">
                <div class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate max-w-xs">
                  {{ item.title.length > 50 ? item.title.substring(0, 50) + '...' : item.title }}
                </div>
              </td>

              <!-- Status -->
              <td class="px-6 py-4">
                <span
                  class="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300': item.sync_status === 'synced',
                    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300': item.sync_status === 'pending',
                    'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300': item.sync_status === 'error',
                  }"
                >
                  {{ item.sync_status.toUpperCase() }}
                </span>
              </td>

              <!-- Retry Count -->
              <td class="px-6 py-4">
                <div class="text-sm text-slate-600 dark:text-slate-400">{{ item.retry_count }}</div>
              </td>

              <!-- Last Sync -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-slate-600 dark:text-slate-400">{{ formatDate(item.last_sync_at) }}</div>
              </td>

              <!-- Created -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-slate-600 dark:text-slate-400">{{ formatDate(item.created_at) }}</div>
              </td>

              <!-- Actions -->
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex justify-end gap-2">
                  <!-- Download -->
                  <a :href="item.presigned_file_url" download @click.stop>
                    <woot-button
                      variant="clear"
                      icon="download"
                      size="small"
                      class="hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full"
                    />
                  </a>
                  <!-- Delete -->
                  <woot-button
                    @click.stop="handleDelete(item)"
                    variant="clear"
                    icon="delete"
                    size="small"
                    color-scheme="alert"
                    class="hover:bg-red-50 dark:hover:bg-red-900 rounded-full"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>